/**
 * TanStack Query hooks for admin telegram users management
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adminUsersApi,
  type CreateTelegramUserDto,
  type UpdateTelegramUserDto,
} from '@/shared/api/admin-users-api';

const QUERY_KEY = ['admin', 'telegram-users'];

/**
 * List all telegram users
 */
export function useTelegramUsersList() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => adminUsersApi.list(),
    staleTime: 1000 * 60, // 1 minute
  });
}

/**
 * Create a new telegram user
 */
export function useCreateTelegramUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTelegramUserDto) => adminUsersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/**
 * Update telegram user
 */
export function useUpdateTelegramUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      telegramUserId,
      data,
    }: {
      telegramUserId: string;
      data: UpdateTelegramUserDto;
    }) => adminUsersApi.update(telegramUserId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/**
 * Delete telegram user
 */
export function useDeleteTelegramUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (telegramUserId: string) => adminUsersApi.delete(telegramUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/**
 * Get brand subscriptions for a user
 */
export function useBrandSubscriptions(telegramUserId: string) {
  return useQuery({
    queryKey: ['admin', 'brand-subscriptions', telegramUserId],
    queryFn: () => adminUsersApi.getSubscriptions(telegramUserId),
    staleTime: 1000 * 60,
  });
}

/**
 * Set brand subscriptions for a user
 */
export function useSetBrandSubscriptions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      telegramUserId,
      brands,
    }: {
      telegramUserId: string;
      brands: string[];
    }) => adminUsersApi.setSubscriptions(telegramUserId, brands),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'brand-subscriptions', variables.telegramUserId],
      });
    },
  });
}

/**
 * Get available affiliate IDs (distinct from events)
 */
export function useAvailableAffiliates() {
  return useQuery({
    queryKey: ['admin', 'available-affiliates'],
    queryFn: () => adminUsersApi.getAvailableAffiliates(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Get affiliate subscriptions for a user
 */
export function useAffiliateSubscriptions(telegramUserId: string) {
  return useQuery({
    queryKey: ['admin', 'affiliate-subscriptions', telegramUserId],
    queryFn: () => adminUsersApi.getAffiliateSubscriptions(telegramUserId),
    staleTime: 1000 * 60,
  });
}

/**
 * Set affiliate subscriptions for a user
 */
export function useSetAffiliateSubscriptions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      telegramUserId,
      affiliateIds,
    }: {
      telegramUserId: string;
      affiliateIds: string[];
    }) => adminUsersApi.setAffiliateSubscriptions(telegramUserId, affiliateIds),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'affiliate-subscriptions', variables.telegramUserId],
      });
    },
  });
}
